import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { productionProfilesClient } from '@/api'
import { config } from '@/config'
import { UpdateProductionProfileDatasetRequest } from '@/interfaces'

const BASE_ROUTE = config.links.library.productionProfiles
const BASE_QUERY_KEY = productionProfilesClient.QUERY_KEY

export const useProductionProfileDetails = (datasetId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, datasetId]

  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()

  const getProductionProfileNamesQuery = useQuery({
    queryKey: [...BASE_QUERY_KEY, { lockedOnly: false }],
    queryFn: () => productionProfilesClient.getAll(),
    select: data => data.map(x => x.name || '').filter(Boolean)
  })

  const getProductionProfileQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => productionProfilesClient.getById(datasetId),
    enabled: !!datasetId
  })

  const updateDatasetMutation = useMutation({
    mutationFn: (req: UpdateProductionProfileDatasetRequest) =>
      productionProfilesClient.updateDataset(datasetId, req),
    onSuccess: async () => {
      showSuccess('Dataset successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: (datasetName: string) =>
      productionProfilesClient.cloneDataset({ datasetId, name: datasetName }),
    onSuccess: clonedDatasetId => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${clonedDatasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: () => productionProfilesClient.removeDataset(datasetId),
    onSuccess: () => {
      showSuccess('Dataset successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const lockDatasetMutation = useMutation({
    mutationFn: () => productionProfilesClient.lockDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully locked')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const archiveDatasetMutation = useMutation({
    mutationFn: () => productionProfilesClient.archiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully archived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const unArchiveDatasetMutation = useMutation({
    mutationFn: () => productionProfilesClient.unArchiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully unarchived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    datasetInfo: getProductionProfileQuery.data,
    allDatasetNames: getProductionProfileNamesQuery.data || [],
    isFetching: getProductionProfileQuery.isFetching,
    updateDataset: updateDatasetMutation.mutate,
    isUpdateDatasetInProgress: updateDatasetMutation.isPending,
    cloneDataset: (datasetName: string, onSuccess?: () => void) =>
      cloneDatasetMutation.mutate(datasetName, { onSuccess }),
    isCloneDatasetInProgress: cloneDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    lockDataset: lockDatasetMutation.mutate,
    archiveDataset: archiveDatasetMutation.mutate,
    unArchiveDataset: unArchiveDatasetMutation.mutate,
    getDownloadFileUrl: productionProfilesClient.getDownloadFileUrl
  }
}
